import axios from 'axios';

import Modules from 'root/apps/shared_modules/Modules';
import { trackAsync } from 'root/apps/tracking/trackEvent';

import { B2B_CALL_ROOT, buildImportMSRP, findMSRP, flashError, flashSuccess } from '../utils';
import { B2BTrackingEvents } from './enums';
import { B2BImportItem, B2BItem, B2BMatrixContextItem, B2BMatrixImportBody } from './types';

function buildImportItem(b2bItem: B2BItem): B2BImportItem {
  const b2bMsrp = findMSRP(b2bItem);

  const importItem: B2BImportItem = {
    b2b_catalog_uuid: b2bItem.id,
    upc: b2bItem.upc,
    description: b2bItem.name,
    brand_name: b2bItem.brand_name,
    color: b2bItem.color || null,
    size: b2bItem.size || null,
    season: b2bItem.season || null,
    image_url: b2bItem.images?.[0]?.image_url,
  };

  const msrpImport = buildImportMSRP(b2bMsrp);
  if (msrpImport) {
    importItem.msrp = msrpImport;
  }

  if (b2bItem.images?.length) {
    importItem.image_url = b2bItem.images[0].image_url;
  }

  return importItem;
}

export function useMatrixImport(purchaseID?: number, vendorID?: number) {
  const { trackB2BEvent } = useB2BTracking();
  return async (items: B2BMatrixContextItem[], matrixID: string, useDefaultCost: boolean) => {
    window.showNotifier('working');

    const getPriceIndex = useDefaultCost ? (_: number) => 0 : (i: number) => i;
    const importItems = items.map((item, i): B2BImportItem => {
      const importItem = {
        ...buildImportItem(item.b2bItem),
        group_id: item.b2bItem.group_id,
      };

      if (purchaseID) {
        if (vendorID) {
          importItem.vendor_id = vendorID;
        }

        importItem.defaultCost = {
          value: items[getPriceIndex(i)].cost,
          currency: window.merchantos.locale.currency_code,
        };
        importItem.defaultPrice = {
          value: items[getPriceIndex(i)].price,
          currency: window.merchantos.locale.currency_code,
        };
      }

      if (item.b2bItem.varies_by) {
        importItem.varies_by = item.b2bItem.varies_by;
      }

      return importItem;
    });

    const importBody: B2BMatrixImportBody = {
      items: importItems,
      matrixID: matrixID || '',
    };

    if (purchaseID) {
      importBody.purchaseOrderID = purchaseID;
    }

    try {
      const {
        data: {
          data: {
            item: { matrixID: newMatrixID },
          },
        },
      } = await axios.post('/admin/item/b2b-catalog/group/import', importBody);
      window.hideNotifier('working');

      // We don't navigate away for purchase order additions, so the user can add more items
      if (purchaseID) {
        const plural = importItems.length !== 1 ? 's' : '';
        window.flashNotifier(
          'success',
          `1 matrix created and ${importItems.length} item${plural} added to PO.`,
          3
        );
      } else {
        window.flashNotifier('success', 'Matrix created successfully.', 3);
        window.navigateToContent('view', 'item_group.views.item_group', newMatrixID);
      }
      if (Modules.hasModule('reports.capital.roles')) {
        trackB2BEvent(B2BTrackingEvents.B2BMatrixImported, {
          matrix_id: newMatrixID,
          vendor_id: vendorID,
          purchase_id: purchaseID,
        });
      }
    } catch (error) {
      window.showNotifier('warning', 'Failed to create matrix.');
    }
  };
}

export function useB2BInsert(b2bItem: B2BItem, purchaseID?: number, vendorID?: number) {
  const { trackB2BEvent } = useB2BTracking();
  return async () => {
    const importItem: B2BImportItem = buildImportItem(b2bItem);

    if (purchaseID && vendorID) {
      importItem.vendor_id = vendorID;
    }

    try {
      const result = await axios.post(B2B_CALL_ROOT, importItem);
      const itemID = result.data?.data?.item?.itemID;
      if (itemID) {
        window.navigateToContent('view', 'item.views.item', itemID);
        flashSuccess();
        if (Modules.hasModule('reports.capital.roles')) {
          trackB2BEvent(B2BTrackingEvents.B2BItemImported, {
            item_id: itemID,
            vendor_id: vendorID,
            purchase_id: purchaseID,
          });
        }
      } else {
        flashError();
      }
    } catch {
      flashError();
    }
  };
}

export const useB2BTracking = () => {
  const trackB2BEvent = async (eventName: string, eventProps: any): Promise<void> => {
    await trackAsync(eventName, {
      ...eventProps,
      catalog_type: 'b2b',
    });
  };

  return {
    trackB2BEvent,
  };
};
